import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { Evidence } from '@/types';
import { EvidenceDrawer } from './EvidenceDrawer';

/* ----------------------------------------------------------------------------
 * <Score> — the evidence-first primitive (brief §6.1, §6.4).
 *
 * "Never a bare number" is enforced at the TYPE level: `evidence` is REQUIRED,
 * so a score cannot be rendered without its rationale. Clicking the score opens
 * the Evidence Drawer. Tabular mono figures, per-engine accent affordance.
 * -------------------------------------------------------------------------- */

export interface ScoreProps {
  value: number | string;
  /** REQUIRED — a bare, unexplained number must be impossible to construct. */
  evidence: Evidence;
  label: string;
  /** 0..100 scale suffix, e.g. "/100"; omit for non-scaled values. */
  outOf?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizes = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-2xl',
};

export function Score({ value, evidence, label, outOf, size = 'md', className }: ScoreProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`${label}: ${value}${outOf ? ` of ${outOf}` : ''}. View evidence.`}
        className={cn(
          'group inline-flex items-center gap-1.5 rounded-sm px-1 font-mono font-semibold tabular-nums',
          'text-text-primary transition-colors duration-fast hover:text-engine',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai',
          sizes[size],
          className,
        )}
        data-tabular
      >
        <span>{value}</span>
        {outOf && <span className="text-xs font-normal text-text-tertiary">/{outOf}</span>}
        {/* evidence affordance — the "one glance away" dotted underline mark */}
        <span
          aria-hidden
          className="ml-0.5 inline-block h-3 w-3 rounded-full border border-engine/50 text-[8px] leading-3 text-engine opacity-60 transition-opacity group-hover:opacity-100"
          title="Evidence"
        >
          ⓘ
        </span>
      </button>
      <EvidenceDrawer
        open={open}
        onClose={() => setOpen(false)}
        label={label}
        score={outOf ? `${value}/${outOf}` : value}
        evidence={evidence}
      />
    </>
  );
}
